import React, { useEffect, useState } from 'react';
import {
    KeyRound, Plus, Trash2, RefreshCw, Zap, Loader, X, CheckCircle,
    AlertCircle, Building2, Eye, EyeOff, Pencil, ToggleLeft, ToggleRight,
    Radar,
} from 'lucide-react';
import { adminService, SharedCatalogConfig, SharedCatalogSessionStatus } from '../services/AdminService';
import { settingsService } from '../services/SettingsService';

// Human label for the keepalive session state of a shared catalog
const sessionLabel = (s: SharedCatalogSessionStatus | null | undefined): { text: string; ok: boolean | null } => {
    if (!s) return { text: 'session: not checked yet', ok: null };
    const ageMin = Math.round((Date.now() / 1000 - s.checked_at) / 60);
    const checked = ageMin < 1 ? 'just now' : `${ageMin}m ago`;
    if (s.status !== 'success') return { text: `session error (${checked}): ${s.message || 'unknown'}`, ok: false };
    if (s.expires_at) {
        const hrs = (s.expires_at - Date.now() / 1000) / 3600;
        const life = hrs >= 1 ? `${Math.round(hrs)}h` : `${Math.max(0, Math.round(hrs * 60))}m`;
        return { text: `session ok (${checked}) · token expires in ${life}`, ok: true };
    }
    return { text: `session ok (${checked})`, ok: true };
};

// Platform-admin manager for shared catalog credentials. A shared catalog is
// defined once (credentials stored in Google Secret Manager) and assigned to
// organizations; assigned orgs see it as a locked entry in their catalog
// settings. Credentials are write-only: they can be set or rotated here but
// are never displayed back.

const CATALOG_IMPLEMENTATIONS = [
    { value: 'snaponepc', label: 'Snapon EPC' },
    { value: 'saicmotor', label: 'Saic Motor' },
    { value: 'excelerator', label: 'Excelerator' },
    { value: 'microcat', label: 'Microcat EPC' },
    { value: 'xentry', label: 'XENTRY (Daimler Truck)' },
    { value: 'partslink24', label: 'partslink24' },
    { value: 'asaweb', label: 'Mitsubishi ASA (myASAweb)' },
    { value: 'kgmobility', label: 'KG Mobility / SsangYong EPC' },
    { value: 'geely', label: 'Geely EPC (lepc.geely.com)' },
];
// Same field rules as the org catalog form (CatalogSettingsPanel)
const needsCompanyId = (impl: string) => ['partslink24', 'asaweb'].includes(impl);
const needsBrands = (impl: string) => impl === 'partslink24';

interface SharedCatalogsTabProps {
    organizations: any[];
}

interface FormState {
    catalog_name: string;
    catalog_implementation: string;
    catalog_user: string;
    catalog_password: string;
    catalog_company: string;
    brands: string[];
    slug: string;
    email_otp_enabled: boolean;
    email_otp_from: string;
    email_otp_subject: string;
}

const EMPTY_FORM: FormState = {
    catalog_name: '',
    catalog_implementation: '',
    catalog_user: '',
    catalog_password: '',
    catalog_company: '',
    brands: [],
    slug: '',
    email_otp_enabled: false,
    email_otp_from: '',
    email_otp_subject: '',
};

const SharedCatalogsTab: React.FC<SharedCatalogsTabProps> = ({ organizations }) => {
    const [configs, setConfigs] = useState<SharedCatalogConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [actionMsg, setActionMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

    // Create / edit form
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [showPassword, setShowPassword] = useState(false);
    const [saving, setSaving] = useState(false);
    const [wmiBrands, setWmiBrands] = useState<string[]>([]);
    const [brandFilter, setBrandFilter] = useState('');

    // Per-config busy indicators
    const [testingId, setTestingId] = useState<number | null>(null);
    const [testResult, setTestResult] = useState<{ id: number; ok: boolean; text: string } | null>(null);
    const [assigningId, setAssigningId] = useState<number | null>(null);
    const [assignOrgId, setAssignOrgId] = useState<string>('');

    // Session keeper (background login checker) state
    const [keeperEnabled, setKeeperEnabled] = useState<boolean | null>(null);
    const [keeperEnvGate, setKeeperEnvGate] = useState(true);
    const [keeperInterval, setKeeperInterval] = useState<number | null>(null);
    const [keeperToggling, setKeeperToggling] = useState(false);

    const notify = (text: string, type: 'success' | 'error') => {
        setActionMsg({ text, type });
        setTimeout(() => setActionMsg(null), 4000);
    };

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const resp = await adminService.getSharedCatalogs();
            setConfigs(resp.data.shared_catalogs);
            try {
                const keeper = await adminService.getSessionKeeperState();
                setKeeperEnabled(keeper.data.enabled);
                setKeeperEnvGate(keeper.data.env_gate);
                setKeeperInterval(keeper.data.interval_seconds);
            } catch { /* keeper state is informational — don't fail the tab */ }
        } catch (e: any) {
            setError(e?.response?.data?.detail || e?.message || 'Failed to load shared catalogs');
        } finally {
            setLoading(false);
        }
    };

    const toggleKeeper = async () => {
        if (keeperEnabled === null || keeperToggling) return;
        setKeeperToggling(true);
        try {
            const next = !keeperEnabled;
            await adminService.setSessionKeeperEnabled(next);
            setKeeperEnabled(next);
            notify(
                next
                    ? 'Session checker enabled — sessions refresh on every sweep'
                    : 'Session checker disabled — sessions will only refresh lazily on user queries',
                'success'
            );
        } catch (e: any) {
            notify(e?.response?.data?.detail || 'Failed to toggle session checker', 'error');
        } finally {
            setKeeperToggling(false);
        }
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (!showForm || !needsBrands(form.catalog_implementation) || wmiBrands.length > 0) return;
        settingsService.getWmiBrands()
            .then(res => setWmiBrands(res.data?.brands || []))
            .catch(err => console.error('Failed to load WMI brands:', err));
    }, [showForm, form.catalog_implementation]);

    const openCreate = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setShowForm(true);
        setShowPassword(false);
    };

    const openEdit = (config: SharedCatalogConfig) => {
        setEditingId(config.id);
        setForm({
            catalog_name: config.catalog_name,
            catalog_implementation: config.catalog_implementation,
            catalog_user: '',
            catalog_password: '',
            catalog_company: '',
            brands: config.brands || [],
            slug: config.slug,
            email_otp_enabled: !!config.email_otp?.enabled,
            email_otp_from: String(config.email_otp?.from || config.email_otp?.match?.sender?.value || ''),
            email_otp_subject: String(config.email_otp?.subject || config.email_otp?.match?.subject?.value || ''),
        });
        setShowForm(true);
        setShowPassword(false);
    };

    const submitForm = async () => {
        if (!form.catalog_name || !form.catalog_implementation) {
            notify('Catalog name and implementation are required', 'error');
            return;
        }
        const credsProvided = !!(form.catalog_user || form.catalog_password);
        if (credsProvided && (!form.catalog_user || !form.catalog_password)) {
            notify('Provide both username and password to set credentials', 'error');
            return;
        }
        setSaving(true);
        try {
            const emailOtp: Record<string, any> = {
                enabled: form.email_otp_enabled,
                from: form.email_otp_from.trim(),
                subject: form.email_otp_subject.trim(),
            };
            if (editingId === null) {
                if (!credsProvided) {
                    notify('Credentials are required for a new shared catalog', 'error');
                    return;
                }
                await adminService.createSharedCatalog({
                    catalog_name: form.catalog_name,
                    catalog_implementation: form.catalog_implementation,
                    catalog_user: form.catalog_user,
                    catalog_password: form.catalog_password,
                    slug: form.slug || undefined,
                    email_otp: emailOtp,
                    catalog_company: form.catalog_company,
                    brands: needsBrands(form.catalog_implementation) ? form.brands : null,
                });
                notify('Shared catalog created', 'success');
            } else {
                await adminService.updateSharedCatalog(editingId, {
                    catalog_name: form.catalog_name,
                    catalog_implementation: form.catalog_implementation,
                    email_otp: emailOtp,
                    brands: needsBrands(form.catalog_implementation) ? form.brands : null,
                    ...(credsProvided ? {
                        catalog_user: form.catalog_user,
                        catalog_password: form.catalog_password,
                        catalog_company: form.catalog_company,
                    } : {}),
                });
                notify(credsProvided ? 'Shared catalog updated, credentials rotated' : 'Shared catalog updated', 'success');
            }
            setShowForm(false);
            await load();
        } catch (e: any) {
            notify(e?.response?.data?.detail || 'Failed to save shared catalog', 'error');
        } finally {
            setSaving(false);
        }
    };

    const toggleCatalogKeepalive = async (config: SharedCatalogConfig) => {
        try {
            const next = !config.keepalive_enabled;
            await adminService.updateSharedCatalog(config.id, { keepalive_enabled: next });
            notify(
                next
                    ? `Session checker enabled for '${config.slug}'`
                    : `Session checker disabled for '${config.slug}' — its session refreshes only on user queries`,
                'success'
            );
            await load();
        } catch (e: any) {
            notify(e?.response?.data?.detail || 'Failed to toggle catalog checker', 'error');
        }
    };

    const toggleActive = async (config: SharedCatalogConfig) => {
        try {
            await adminService.updateSharedCatalog(config.id, { is_active: !config.is_active });
            notify(config.is_active ? `'${config.slug}' deactivated` : `'${config.slug}' activated`, 'success');
            await load();
        } catch (e: any) {
            notify(e?.response?.data?.detail || 'Failed to update status', 'error');
        }
    };

    const hardDelete = async (config: SharedCatalogConfig) => {
        if (!window.confirm(
            `Permanently delete shared catalog '${config.slug}'?\n\nThis removes the Secret Manager secret and all ${config.assignments.length} organization assignment(s). Assigned organizations will lose access to this catalog unless they configure their own credentials.`
        )) return;
        try {
            await adminService.deleteSharedCatalog(config.id, true);
            notify(`'${config.slug}' deleted`, 'success');
            await load();
        } catch (e: any) {
            notify(e?.response?.data?.detail || 'Failed to delete shared catalog', 'error');
        }
    };

    const testConnection = async (config: SharedCatalogConfig) => {
        setTestingId(config.id);
        setTestResult(null);
        try {
            await adminService.testSharedCatalog(config.id);
            setTestResult({ id: config.id, ok: true, text: 'Connection successful' });
        } catch (e: any) {
            setTestResult({
                id: config.id,
                ok: false,
                text: e?.response?.data?.detail || e?.message || 'Connection failed',
            });
        } finally {
            setTestingId(null);
        }
    };

    const assignOrg = async (config: SharedCatalogConfig) => {
        const orgId = parseInt(assignOrgId, 10);
        if (!orgId) return;
        try {
            await adminService.assignSharedCatalog(config.id, orgId);
            notify('Organization assigned', 'success');
            setAssignOrgId('');
            setAssigningId(null);
            await load();
        } catch (e: any) {
            notify(e?.response?.data?.detail || 'Failed to assign organization', 'error');
        }
    };

    const unassignOrg = async (config: SharedCatalogConfig, organizationId: number, organizationName: string) => {
        if (!window.confirm(`Remove '${config.slug}' from ${organizationName}?`)) return;
        try {
            await adminService.unassignSharedCatalog(config.id, organizationId);
            notify('Assignment removed', 'success');
            await load();
        } catch (e: any) {
            notify(e?.response?.data?.detail || 'Failed to remove assignment', 'error');
        }
    };

    const unassignedOrgs = (config: SharedCatalogConfig) => {
        const assigned = new Set(config.assignments.map(a => a.organization_id));
        return organizations.filter(o => !assigned.has(o.id));
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-indigo-500" />
                            Shared Catalog Credentials
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Define catalog credentials once and assign them to organizations. Credentials are stored in
                            Secret Manager and never shown again. Assigned orgs see the catalog as a locked entry; an
                            org-level entry with its own credentials still overrides the shared one.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {keeperEnabled !== null && (
                            <button
                                onClick={toggleKeeper}
                                disabled={keeperToggling || !keeperEnvGate}
                                title={!keeperEnvGate
                                    ? 'Disabled by deployment (CATALOG_SESSION_KEEPER=false) — the toggle has no effect'
                                    : keeperEnabled
                                        ? `Background login checker is ON (sweeps every ${keeperInterval ?? '?'}s). Click to pause.`
                                        : 'Background login checker is OFF — sessions refresh only when a user queries. Click to resume.'}
                                className={`inline-flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 ${keeperEnabled
                                    ? 'text-green-700 bg-green-50 border-green-200 hover:bg-green-100'
                                    : 'text-gray-500 bg-gray-50 border-gray-200 hover:bg-gray-100'}`}
                            >
                                {keeperToggling
                                    ? <Loader className="w-3.5 h-3.5 animate-spin" />
                                    : <Radar className="w-3.5 h-3.5" />}
                                Session checker: {keeperEnabled ? 'On' : 'Off'}
                                {keeperEnabled
                                    ? <ToggleRight className="w-4 h-4" />
                                    : <ToggleLeft className="w-4 h-4" />}
                            </button>
                        )}
                        <button
                            onClick={load}
                            disabled={loading}
                            className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Refresh"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                            onClick={openCreate}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                            <Plus className="w-4 h-4" />
                            New Shared Catalog
                        </button>
                    </div>
                </div>

                {actionMsg && (
                    <div className={`px-6 py-3 border-b text-sm flex items-center gap-2 ${actionMsg.type === 'success'
                        ? 'bg-green-50 border-green-100 text-green-700'
                        : 'bg-red-50 border-red-100 text-red-700'}`}>
                        {actionMsg.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                        {actionMsg.text}
                    </div>
                )}
                {error && (
                    <div className="px-6 py-3 text-sm text-red-600 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" /> {error}
                    </div>
                )}
            </div>

            {/* Create / edit form */}
            {showForm && (
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-medium text-gray-900">
                            {editingId === null ? 'New Shared Catalog' : `Edit '${form.slug}'`}
                        </h3>
                        <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-700">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Catalog Name</label>
                            <input
                                type="text"
                                value={form.catalog_name}
                                onChange={e => setForm(f => ({ ...f, catalog_name: e.target.value }))}
                                placeholder="e.g., Kia, MG"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Implementation</label>
                            <select
                                value={form.catalog_implementation}
                                onChange={e => setForm(f => ({ ...f, catalog_implementation: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                            >
                                <option value="">— select implementation —</option>
                                {CATALOG_IMPLEMENTATIONS.map(impl => (
                                    <option key={impl.value} value={impl.value}>{impl.label}</option>
                                ))}
                            </select>
                        </div>
                        {needsBrands(form.catalog_implementation) && (() => {
                            const selected = form.brands;
                            const options = Array.from(new Set([...wmiBrands, ...selected]))
                                .filter(b => b.toLowerCase().includes(brandFilter.toLowerCase()))
                                .sort((a, b) => a.localeCompare(b));
                            const toggle = (brand: string) => setForm(f => ({
                                ...f, brands: f.brands.includes(brand) ? f.brands.filter(b => b !== brand) : [...f.brands, brand],
                            }));
                            return (
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Brands served by this catalog
                                        <span className="ml-2 text-xs font-normal text-gray-500">{selected.length} selected</span>
                                    </label>
                                    {selected.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5 mb-2">
                                            {selected.map(b => (
                                                <button key={b} type="button" onClick={() => toggle(b)}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-gray-900 text-white hover:bg-gray-700">
                                                    {b}<span aria-hidden>×</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <input
                                        type="text"
                                        value={brandFilter}
                                        onChange={e => setBrandFilter(e.target.value)}
                                        placeholder="Filter brands…"
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-t-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                    />
                                    <div className="max-h-56 overflow-y-auto border border-t-0 border-gray-300 rounded-b-lg bg-white grid grid-cols-2 sm:grid-cols-3 gap-x-2">
                                        {options.map(b => (
                                            <label key={b} className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer">
                                                <input type="checkbox" checked={selected.includes(b)} onChange={() => toggle(b)} />
                                                <span className="truncate">{b}</span>
                                            </label>
                                        ))}
                                        {options.length === 0 && (
                                            <p className="col-span-full px-3 py-2 text-xs text-gray-500">
                                                {wmiBrands.length === 0 ? 'Loading brands…' : 'No brand matches'}
                                            </p>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">A VIN decoding to any selected brand is searched in this catalog.</p>
                                </div>
                            );
                        })()}
                        {needsCompanyId(form.catalog_implementation) && (
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Company ID {editingId !== null && <span className="text-gray-400 font-normal">(sent with username + password)</span>}
                                </label>
                                <input
                                    type="text"
                                    value={form.catalog_company}
                                    onChange={e => setForm(f => ({ ...f, catalog_company: e.target.value }))}
                                    placeholder="Enter company / client id"
                                    autoComplete="off"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Username {editingId !== null && <span className="text-gray-400 font-normal">(leave blank to keep current)</span>}
                            </label>
                            <input
                                type="text"
                                value={form.catalog_user}
                                onChange={e => setForm(f => ({ ...f, catalog_user: e.target.value }))}
                                autoComplete="off"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Password {editingId !== null && <span className="text-gray-400 font-normal">(leave blank to keep current)</span>}
                            </label>
                            <div className="relative">
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={form.catalog_password}
                                    onChange={e => setForm(f => ({ ...f, catalog_password: e.target.value }))}
                                    autoComplete="new-password"
                                    className="w-full px-3 py-2 pr-9 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                                <button
                                    type="button"
                                    tabIndex={-1}
                                    onClick={() => setShowPassword(p => !p)}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        {editingId === null && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Slug <span className="text-gray-400 font-normal">(optional, defaults from name)</span>
                                </label>
                                <input
                                    type="text"
                                    value={form.slug}
                                    onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
                                    placeholder="e.g., kia-latam"
                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                />
                            </div>
                        )}
                        <div className="md:col-span-2 rounded-lg border border-gray-200 p-4">
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <input type="checkbox" checked={form.email_otp_enabled} onChange={e => setForm(f => ({ ...f, email_otp_enabled: e.target.checked }))} />
                                Enable email OTP
                            </label>
                            {form.email_otp_enabled && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <label className="text-sm text-gray-700">From
                                    <input type="email" value={form.email_otp_from} onChange={e => setForm(f => ({ ...f, email_otp_from: e.target.value }))} placeholder="forwarder@example.com" className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
                                </label>
                                <label className="text-sm text-gray-700">Subject contains
                                    <input value={form.email_otp_subject} onChange={e => setForm(f => ({ ...f, email_otp_subject: e.target.value }))} placeholder="MFA Email Verification" className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg" />
                                </label>
                                <p className="md:col-span-2 text-xs text-gray-500">The verification code is extracted automatically with AI.</p>
                            </div>}
                        </div>
                    </div>
                    <div className="mt-5 flex justify-end gap-3">
                        <button
                            onClick={() => setShowForm(false)}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={submitForm}
                            disabled={saving}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                        >
                            {saving && <Loader className="w-4 h-4 animate-spin" />}
                            {editingId === null ? 'Create' : 'Save'}
                        </button>
                    </div>
                </div>
            )}

            {/* List */}
            {loading ? (
                <div className="flex items-center justify-center py-16 text-gray-500 gap-2">
                    <Loader className="w-5 h-5 animate-spin" /> Loading shared catalogs...
                </div>
            ) : configs.length === 0 ? (
                <div className="bg-white rounded-lg shadow px-6 py-12 text-center text-sm text-gray-500">
                    No shared catalogs yet. Create one to stop duplicating credentials across organizations.
                </div>
            ) : (
                <div className="space-y-4">
                    {configs.map(config => (
                        <div key={config.id} className={`bg-white rounded-lg shadow ${!config.is_active ? 'opacity-60' : ''}`}>
                            <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-3">
                                        <h3 className="text-base font-semibold text-gray-900">{config.catalog_name}</h3>
                                        <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">{config.slug}</span>
                                        <span className="text-xs text-gray-500">{config.catalog_implementation}</span>
                                        {!config.is_active && (
                                            <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">Inactive</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">
                                        {config.assignments.length} organization(s) assigned
                                        {config.created_by ? ` · created by ${config.created_by}` : ''}
                                    </p>
                                    {config.is_active && (() => {
                                        if (!config.keepalive_enabled) {
                                            return (
                                                <p className="text-xs mt-1 flex items-center gap-1.5 text-gray-400">
                                                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-gray-300"></span>
                                                    session checker off — refreshes only on user queries
                                                </p>
                                            );
                                        }
                                        const s = sessionLabel(config.session);
                                        return (
                                            <p className={`text-xs mt-1 flex items-center gap-1.5 ${s.ok === false ? 'text-red-600' : s.ok ? 'text-green-600' : 'text-gray-400'}`}>
                                                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${s.ok === false ? 'bg-red-500' : s.ok ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                                                {s.text}
                                            </p>
                                        );
                                    })()}
                                    {testResult?.id === config.id && (
                                        <p className={`text-xs mt-1 flex items-center gap-1.5 ${testResult.ok ? 'text-green-600' : 'text-red-600'}`}>
                                            {testResult.ok
                                                ? <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                                : <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />}
                                            {testResult.text}
                                        </p>
                                    )}
                                </div>
                                <div className="flex items-center gap-1.5 flex-shrink-0">
                                    <button
                                        onClick={() => testConnection(config)}
                                        disabled={testingId === config.id}
                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        {testingId === config.id ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                                        Test
                                    </button>
                                    <button
                                        onClick={() => toggleCatalogKeepalive(config)}
                                        className={`p-2 rounded-lg ${config.keepalive_enabled
                                            ? 'text-green-600 hover:text-green-800 hover:bg-green-50'
                                            : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
                                        title={config.keepalive_enabled
                                            ? 'Session checker ON for this catalog — click to disable'
                                            : 'Session checker OFF for this catalog — click to enable'}
                                    >
                                        <Radar className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => openEdit(config)}
                                        className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                                        title="Edit / rotate credentials"
                                    >
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => toggleActive(config)}
                                        className="p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                                        title={config.is_active ? 'Deactivate (orgs stop receiving it)' : 'Activate'}
                                    >
                                        {config.is_active
                                            ? <ToggleRight className="w-4 h-4 text-green-600" />
                                            : <ToggleLeft className="w-4 h-4" />}
                                    </button>
                                    <button
                                        onClick={() => hardDelete(config)}
                                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                                        title="Delete permanently"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Assignments */}
                            <div className="px-6 py-4">
                                <div className="flex flex-wrap items-center gap-2">
                                    {config.assignments.map(a => (
                                        <span
                                            key={a.organization_id}
                                            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 pl-2.5 pr-1 py-1 rounded-full"
                                        >
                                            <Building2 className="w-3 h-3" />
                                            {a.organization_name}
                                            <button
                                                onClick={() => unassignOrg(config, a.organization_id, a.organization_name)}
                                                className="p-0.5 text-blue-400 hover:text-red-600 rounded-full"
                                                title="Remove assignment"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </span>
                                    ))}

                                    {assigningId === config.id ? (
                                        <span className="inline-flex items-center gap-1.5">
                                            <select
                                                value={assignOrgId}
                                                onChange={e => setAssignOrgId(e.target.value)}
                                                className="text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                                autoFocus
                                            >
                                                <option value="">— select org —</option>
                                                {unassignedOrgs(config).map(o => (
                                                    <option key={o.id} value={o.id}>{o.name}</option>
                                                ))}
                                            </select>
                                            <button
                                                onClick={() => assignOrg(config)}
                                                disabled={!assignOrgId}
                                                className="text-xs font-medium text-white bg-indigo-600 px-2.5 py-1.5 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                                            >
                                                Assign
                                            </button>
                                            <button
                                                onClick={() => { setAssigningId(null); setAssignOrgId(''); }}
                                                className="p-1 text-gray-400 hover:text-gray-700"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </span>
                                    ) : (
                                        <button
                                            onClick={() => { setAssigningId(config.id); setAssignOrgId(''); }}
                                            className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 border border-dashed border-gray-300 px-2.5 py-1 rounded-full hover:text-indigo-600 hover:border-indigo-300"
                                        >
                                            <Plus className="w-3 h-3" />
                                            Assign org
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SharedCatalogsTab;
